import { Dispatch, SetStateAction, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { InternalActions, TableActions, TableViewRow } from './types';

interface UseTableHandlersParams
  extends Pick<TableActions, 'onAddRow' | 'onCopyRow' | 'onDeleteRow'>,
    Pick<InternalActions, 'onStartEdit'> {
  data: TableViewRow[];
  newRows: TableViewRow[];
  setNewRows: Dispatch<SetStateAction<TableViewRow[]>>;
  getDefaultRow?: () => TableViewRow;
  id: string;
}

export function useTableHandlers({
  data,
  newRows,
  setNewRows,
  onStartEdit,
  onAddRow,
  onCopyRow,
  onDeleteRow,
  getDefaultRow,
  id,
}: UseTableHandlersParams) {
  const handleAdd = useCallback(() => {
    const baseRow = getDefaultRow ? getDefaultRow() : { id: uuidv4() };
    const newRow = structuredClone(baseRow);

    setNewRows([newRow]);
    onStartEdit?.(structuredClone(newRow));
    onAddRow?.(newRow, id);
  }, [getDefaultRow, setNewRows, onStartEdit, onAddRow, id]);

  const handleCopy = useCallback(
    (row: TableViewRow) => {
      const copy = { ...row, id: uuidv4() };
      const index = data.findIndex(({ id }) => id === row.id);
      if (index === -1) return;
      const updatedRows = [...data.slice(0, index + 1), copy, ...data.slice(index + 1)];
      onCopyRow?.(updatedRows);
    },
    [data, onCopyRow],
  );

  const handleDelete = useCallback(
    (row: TableViewRow) => {
      const isNewRow = newRows.some(({ id }) => id === row.id);
      if (isNewRow) {
        setNewRows((prev) => prev.filter(({ id }) => id !== row.id));
      }
      onDeleteRow?.(row, id);
    },
    [newRows, setNewRows, onDeleteRow, id],
  );

  return {
    handleAdd,
    handleCopy,
    handleDelete,
  };
}
